import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import { detectDevice, getAgedCareOptimizations } from '../utils/deviceDetection';
import SessionManager from '../services/security/SessionManager';
import KeyManagementService from '../services/security/KeyManagementService';
const SESSION_STORAGE_KEY = 'medfast.session.v1';

/** Login fields start empty; user must type credentials (never pre-filled). */
const EMPTY_LOGIN_CREDENTIALS = { username: '', password: '' };

function readPersistedAuth() {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return { isAuthenticated: false, currentUser: null };
  }
  try {
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return { isAuthenticated: false, currentUser: null };
    const user = JSON.parse(raw);
    if (user && typeof user === 'object' && user.id && user.role) {
      return { isAuthenticated: true, currentUser: user };
    }
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Session] Could not restore session', e);
    }
    try {
      window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } catch (_) {
      /* ignore */
    }
  }
  return { isAuthenticated: false, currentUser: null };
}

const persistedAuth = readPersistedAuth();

// Initial state
const initialState = {
  // Authentication
  isAuthenticated: persistedAuth.isAuthenticated,
  currentUser: persistedAuth.currentUser,
  loginCredentials: { ...EMPTY_LOGIN_CREDENTIALS },
  loginError: '',
  
  // UI State
  activeTab: 'dashboard',
  selectedFacility: 'sunshine',
  selectedWard: 'all',
  selectedResident: null,
  searchQuery: '',
  alertFilter: 'all',
  showPatientHistory: null,
  
  // System Context (for admin dropdown)
  currentSystem: 'agedcare', // 'agedcare'
  systemContext: {
    agedcare: {
      name: 'Aged Care',
      icon: 'Heart',
      color: 'purple',
      terminology: {
        patients: 'Residents',
        clinical: 'Care Records',
        medication: 'Medications',
        careplans: 'Care Plans',
        activities: 'Activities',
        orders: 'Orders',
        quality: 'Quality',
        reports: 'Reports'
      }
    }
  },
  
  // Network & Sync
  networkStatus: 'connected',
  syncProgress: 100,
  
  // Modals & Forms
  showFamilyConnect: false,
  showDrugRegister: false,
  showSupport: false,
  showFacilitySelector: false,
  showDoctorNote: false,
  showNotifications: false,
  showNewEnquiry: false,
  showIncidentReport: false,
  
  // Device & Optimization
  deviceInfo: detectDevice(),
  agedCareOptimizations: getAgedCareOptimizations(detectDevice()),
  
  // Residents
  residents: [],

  // Real-time Data
  currentTime: new Date(),
  weatherData: { temp: 22, condition: 'sunny' },
  notifications: [],
  bulkOrders: [],
  
  // Form States
  noteType: 'progress',
  noteContent: {
    subjective: '',
    objective: '',
    assessment: '',
    plan: '',
  },
  rejectionState: { showModal: false, orderId: null, reason: '' },
  orderStatusFilter: 'all',
};

// Action types
export const ACTIONS = {
  // Authentication
  SET_AUTHENTICATED: 'SET_AUTHENTICATED',
  SET_CURRENT_USER: 'SET_CURRENT_USER',
  SET_LOGIN_CREDENTIALS: 'SET_LOGIN_CREDENTIALS',
  SET_LOGIN_ERROR: 'SET_LOGIN_ERROR',
  LOGOUT: 'LOGOUT',
  
  // UI Navigation
  SET_ACTIVE_TAB: 'SET_ACTIVE_TAB',
  SET_SELECTED_FACILITY: 'SET_SELECTED_FACILITY',
  SET_SELECTED_WARD: 'SET_SELECTED_WARD',
  SET_SELECTED_RESIDENT: 'SET_SELECTED_RESIDENT',
  SET_SEARCH_QUERY: 'SET_SEARCH_QUERY',
  SET_ALERT_FILTER: 'SET_ALERT_FILTER',
  SET_SHOW_PATIENT_HISTORY: 'SET_SHOW_PATIENT_HISTORY',
  
  // System Context
  SET_CURRENT_SYSTEM: 'SET_CURRENT_SYSTEM',
  
  // Modals
  SET_SHOW_FAMILY_CONNECT: 'SET_SHOW_FAMILY_CONNECT',
  SET_SHOW_DRUG_REGISTER: 'SET_SHOW_DRUG_REGISTER',
  SET_SHOW_SUPPORT: 'SET_SHOW_SUPPORT',
  SET_SHOW_FACILITY_SELECTOR: 'SET_SHOW_FACILITY_SELECTOR',
  SET_SHOW_DOCTOR_NOTE: 'SET_SHOW_DOCTOR_NOTE',
  SET_SHOW_NOTIFICATIONS: 'SET_SHOW_NOTIFICATIONS',
  SET_SHOW_NEW_ENQUIRY: 'SET_SHOW_NEW_ENQUIRY',
  SET_SHOW_INCIDENT_REPORT: 'SET_SHOW_INCIDENT_REPORT',
  
  // Network
  SET_NETWORK_STATUS: 'SET_NETWORK_STATUS',
  SET_SYNC_PROGRESS: 'SET_SYNC_PROGRESS',
  
  // Real-time Data
  SET_CURRENT_TIME: 'SET_CURRENT_TIME',
  SET_WEATHER_DATA: 'SET_WEATHER_DATA',
  SET_NOTIFICATIONS: 'SET_NOTIFICATIONS',
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  UPDATE_NOTIFICATION: 'UPDATE_NOTIFICATION',
  SET_BULK_ORDERS: 'SET_BULK_ORDERS',
  ADD_BULK_ORDER: 'ADD_BULK_ORDER',
  REMOVE_BULK_ORDER: 'REMOVE_BULK_ORDER',
  
  // Forms
  SET_NOTE_TYPE: 'SET_NOTE_TYPE',
  SET_NOTE_CONTENT: 'SET_NOTE_CONTENT',
  SET_REJECTION_STATE: 'SET_REJECTION_STATE',
  SET_ORDER_STATUS_FILTER: 'SET_ORDER_STATUS_FILTER',
  
  // Residents CRUD
  SET_RESIDENTS: 'SET_RESIDENTS',
  ADD_RESIDENT: 'ADD_RESIDENT',
  UPDATE_RESIDENT: 'UPDATE_RESIDENT',
  REMOVE_RESIDENT: 'REMOVE_RESIDENT',

  // Device
  UPDATE_DEVICE_INFO: 'UPDATE_DEVICE_INFO',
};

// Reducer
const appReducer = (state, action) => {
  switch (action.type) {
    // Authentication
    case ACTIONS.SET_AUTHENTICATED:
      return { ...state, isAuthenticated: action.payload };
    
    case ACTIONS.SET_CURRENT_USER:
      return { ...state, currentUser: action.payload };
    
    case ACTIONS.SET_LOGIN_CREDENTIALS:
      return { ...state, loginCredentials: action.payload };
    
    case ACTIONS.SET_LOGIN_ERROR:
      return { ...state, loginError: action.payload };
    
    case ACTIONS.LOGOUT:
      return {
        ...state,
        isAuthenticated: false,
        currentUser: null,
        activeTab: 'dashboard',
        selectedFacility: 'sunshine',
        loginCredentials: { ...EMPTY_LOGIN_CREDENTIALS },
        loginError: '',
      };
    
    // UI Navigation
    case ACTIONS.SET_ACTIVE_TAB:
      return { ...state, activeTab: action.payload };
    
    case ACTIONS.SET_SELECTED_FACILITY:
      // Auto-update system context based on facility type
      // The system context will be determined by useFacilityFeatures hook
      // For now, we'll update it synchronously from static config, but components
      // should use useFacilityFeatures for accurate data
      let newSystemContext = state.currentSystem;
      try {
        const facilityTypes = require('../data/facilityTypes');
        newSystemContext = facilityTypes.getFacilitySystemContext(action.payload);
      } catch (e) {
        // If facilityTypes not available, keep current system
        console.warn('Facility types not available:', e);
      }
      return { 
        ...state, 
        selectedFacility: action.payload,
        currentSystem: newSystemContext // Auto-update system context
      };
    
    case ACTIONS.SET_SELECTED_WARD:
      return { ...state, selectedWard: action.payload };
    
    case ACTIONS.SET_SELECTED_RESIDENT:
      return { ...state, selectedResident: action.payload };
    
    case ACTIONS.SET_SEARCH_QUERY:
      return { ...state, searchQuery: action.payload };
    
    case ACTIONS.SET_ALERT_FILTER:
      return { ...state, alertFilter: action.payload };
    
    case ACTIONS.SET_SHOW_PATIENT_HISTORY:
      return { ...state, showPatientHistory: action.payload };
    
    // System Context
    case ACTIONS.SET_CURRENT_SYSTEM:
      return { ...state, currentSystem: action.payload };
    
    // Modals
    case ACTIONS.SET_SHOW_FAMILY_CONNECT:
      return { ...state, showFamilyConnect: action.payload };
    
    case ACTIONS.SET_SHOW_DRUG_REGISTER:
      return { ...state, showDrugRegister: action.payload };
    
    case ACTIONS.SET_SHOW_SUPPORT:
      return { ...state, showSupport: action.payload };
    
    case ACTIONS.SET_SHOW_FACILITY_SELECTOR:
      return { ...state, showFacilitySelector: action.payload };
    
    case ACTIONS.SET_SHOW_DOCTOR_NOTE:
      return { ...state, showDoctorNote: action.payload };
    
    case ACTIONS.SET_SHOW_NOTIFICATIONS:
      return { ...state, showNotifications: action.payload };
    
    case ACTIONS.SET_SHOW_NEW_ENQUIRY:
      return { ...state, showNewEnquiry: action.payload };
    
    case ACTIONS.SET_SHOW_INCIDENT_REPORT:
      return { ...state, showIncidentReport: action.payload };
    
    // Network
    case ACTIONS.SET_NETWORK_STATUS:
      return { ...state, networkStatus: action.payload };
    
    case ACTIONS.SET_SYNC_PROGRESS:
      return { ...state, syncProgress: action.payload };
    
    // Real-time Data
    case ACTIONS.SET_CURRENT_TIME:
      return { ...state, currentTime: action.payload };
    
    case ACTIONS.SET_WEATHER_DATA:
      return { ...state, weatherData: action.payload };
    
    case ACTIONS.SET_NOTIFICATIONS:
      return { ...state, notifications: action.payload };
    
    case ACTIONS.ADD_NOTIFICATION:
      return { 
        ...state, 
        notifications: [action.payload, ...state.notifications] 
      };
    
    case ACTIONS.UPDATE_NOTIFICATION:
      return {
        ...state,
        notifications: state.notifications.map(notification =>
          notification.id === action.payload.id 
            ? { ...notification, ...action.payload.updates }
            : notification
        )
      };
    
    case ACTIONS.SET_BULK_ORDERS:
      return { ...state, bulkOrders: action.payload };
    
    case ACTIONS.ADD_BULK_ORDER:
      return { 
        ...state, 
        bulkOrders: [...state.bulkOrders, action.payload] 
      };
    
    case ACTIONS.REMOVE_BULK_ORDER:
      return {
        ...state,
        bulkOrders: state.bulkOrders.filter(order => order.id !== action.payload)
      };
    
    // Forms
    case ACTIONS.SET_NOTE_TYPE:
      return { ...state, noteType: action.payload };
    
    case ACTIONS.SET_NOTE_CONTENT:
      return { ...state, noteContent: action.payload };
    
    case ACTIONS.SET_REJECTION_STATE:
      return { ...state, rejectionState: action.payload };
    
    case ACTIONS.SET_ORDER_STATUS_FILTER:
      return { ...state, orderStatusFilter: action.payload };
    
    // Residents CRUD
    case ACTIONS.SET_RESIDENTS:
      return { ...state, residents: action.payload };

    case ACTIONS.ADD_RESIDENT:
      return { ...state, residents: [...state.residents, action.payload] };

    case ACTIONS.UPDATE_RESIDENT:
      return {
        ...state,
        residents: state.residents.map((r) =>
          (r.id === action.payload.id) ? { ...r, ...action.payload } : r
        ),
      };

    case ACTIONS.REMOVE_RESIDENT:
      return {
        ...state,
        residents: state.residents.filter((r) => r.id !== action.payload),
      };

    // Device
    case ACTIONS.UPDATE_DEVICE_INFO: {
      const newDeviceInfo = action.payload;
      return {
        ...state,
        deviceInfo: newDeviceInfo,
        agedCareOptimizations: getAgedCareOptimizations(newDeviceInfo),
      };
    }
    
    default:
      return state;
  }
};

// Create context
const AppContext = createContext();

// Provider component
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const deviceBucketRef = useRef(null);

  // Update device info on resize
  useEffect(() => {
    const initial = detectDevice();
    deviceBucketRef.current = initial.isMobile
      ? 'mobile'
      : initial.isTablet
        ? 'tablet'
        : 'desktop';
    if (process.env.NODE_ENV === 'development') {
      console.debug('[Device]', deviceBucketRef.current, {
        width: initial.screenWidth,
        height: initial.screenHeight,
        initial: true,
      });
    }

    const handleResize = () => {
      const newDeviceInfo = detectDevice();
      const bucket = newDeviceInfo.isMobile
        ? 'mobile'
        : newDeviceInfo.isTablet
          ? 'tablet'
          : 'desktop';
      if (
        process.env.NODE_ENV === 'development' &&
        deviceBucketRef.current !== bucket
      ) {
        deviceBucketRef.current = bucket;
        console.debug('[Device]', bucket, {
          width: newDeviceInfo.screenWidth,
          height: newDeviceInfo.screenHeight,
        });
      }
      dispatch({ 
        type: ACTIONS.UPDATE_DEVICE_INFO, 
        payload: newDeviceInfo 
      });
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);
  
  // Real-time updates - optimized to reduce flickering
  useEffect(() => {
    // Update time less frequently to reduce re-renders
    const timeInterval = setInterval(() => {
      dispatch({ 
        type: ACTIONS.SET_CURRENT_TIME, 
        payload: new Date() 
      });
    }, 60000); // Update every minute instead of every second
    
    // Simulate network sync - less frequent
    const syncInterval = setInterval(() => {
      if (Math.random() > 0.95) {
        dispatch({ type: ACTIONS.SET_NETWORK_STATUS, payload: 'syncing' });
        dispatch({ type: ACTIONS.SET_SYNC_PROGRESS, payload: 0 });
        
        const progressInterval = setInterval(() => {
          dispatch((prevState) => {
            const newProgress = prevState.syncProgress + 10;
            if (newProgress >= 100) {
              clearInterval(progressInterval);
              dispatch({ type: ACTIONS.SET_NETWORK_STATUS, payload: 'connected' });
              return { ...prevState, syncProgress: 100 };
            }
            return { ...prevState, syncProgress: newProgress };
          });
        }, 200);
      }
    }, 30000); // Check every 30 seconds
    
    // Simulate weather changes - less frequent
    const weatherInterval = setInterval(() => {
      if (Math.random() > 0.90) {
        const conditions = ['sunny', 'cloudy', 'rainy'];
        dispatch({
          type: ACTIONS.SET_WEATHER_DATA,
          payload: {
            temp: Math.floor(Math.random() * 15) + 15,
            condition: conditions[Math.floor(Math.random() * conditions.length)]
          }
        });
      }
    }, 60000); // Check every minute
    
    return () => {
      clearInterval(timeInterval);
      clearInterval(syncInterval);
      clearInterval(weatherInterval);
    };
  }, []);
  
  // Generate initial notifications
  useEffect(() => {
    const initialNotifications = [
      {
        id: 'NOT001',
        type: 'alert',
        severity: 'high',
        title: 'Fall detected in Room A101',
        message: 'Motion sensors triggered, nurse dispatched',
        timestamp: new Date(Date.now() - 300000),
        read: false
      },
      {
        id: 'NOT002',
        type: 'medication',
        severity: 'medium',
        title: 'Medication review due', 
        message: '5 residents require medication review this week',
        timestamp: new Date(Date.now() - 3600000),
        read: false
      }
    ];
    dispatch({ type: ACTIONS.SET_NOTIFICATIONS, payload: initialNotifications });
  }, []);
  
  const value = {
    state,
    dispatch,
    actions: ACTIONS
  };
  
  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

// Custom hook to use the context
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

// Helper functions for common actions
export const useAppActions = () => {
  const { dispatch } = useAppContext();
  
  return {
    // Authentication
    login: (user) => {
      try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
          window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(user));
        }
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[Session] Persist failed', e);
        }
      }
      dispatch({ type: ACTIONS.SET_CURRENT_USER, payload: user });
      dispatch({ type: ACTIONS.SET_AUTHENTICATED, payload: true });
      dispatch({ type: ACTIONS.SET_LOGIN_ERROR, payload: '' });
      dispatch({ type: ACTIONS.SET_LOGIN_CREDENTIALS, payload: { username: '', password: '' } });
    },
    
    logout: () => {
      try {
        SessionManager.destroySession();
        KeyManagementService.destroy();
        if (typeof window !== 'undefined' && window.sessionStorage) {
          window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
        }
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[Session] Clear failed', e);
        }
      }
      dispatch({ type: ACTIONS.LOGOUT });
    },
    
    setLoginCredentials: (credentials) => {
      dispatch({ type: ACTIONS.SET_LOGIN_CREDENTIALS, payload: credentials });
    },
    
    setLoginError: (error) => {
      dispatch({ type: ACTIONS.SET_LOGIN_ERROR, payload: error });
    },
    
    // Navigation
    setActiveTab: (tab) => {
      dispatch({ type: ACTIONS.SET_ACTIVE_TAB, payload: tab });
    },
    
    setSelectedFacility: (facility) => {
      dispatch({ type: ACTIONS.SET_SELECTED_FACILITY, payload: facility });
    },
    
    setSelectedWard: (ward) => {
      dispatch({ type: ACTIONS.SET_SELECTED_WARD, payload: ward });
    },
    
    setSelectedResident: (resident) => {
      dispatch({ type: ACTIONS.SET_SELECTED_RESIDENT, payload: resident });
    },
    
    setSearchQuery: (query) => {
      dispatch({ type: ACTIONS.SET_SEARCH_QUERY, payload: query });
    },
    
    setAlertFilter: (filter) => {
      dispatch({ type: ACTIONS.SET_ALERT_FILTER, payload: filter });
    },
    
    setShowPatientHistory: (residentId) => {
      dispatch({ type: ACTIONS.SET_SHOW_PATIENT_HISTORY, payload: residentId });
    },
    
    // Modals
    setShowFamilyConnect: (show) => {
      dispatch({ type: ACTIONS.SET_SHOW_FAMILY_CONNECT, payload: show });
    },
    
    setShowDrugRegister: (show) => {
      dispatch({ type: ACTIONS.SET_SHOW_DRUG_REGISTER, payload: show });
    },
    
    setShowSupport: (show) => {
      dispatch({ type: ACTIONS.SET_SHOW_SUPPORT, payload: show });
    },
    
    setShowFacilitySelector: (show) => {
      dispatch({ type: ACTIONS.SET_SHOW_FACILITY_SELECTOR, payload: show });
    },
    
    setShowDoctorNote: (show) => {
      dispatch({ type: ACTIONS.SET_SHOW_DOCTOR_NOTE, payload: show });
    },
    
    setShowNotifications: (show) => {
      dispatch({ type: ACTIONS.SET_SHOW_NOTIFICATIONS, payload: show });
    },
    
    setShowNewEnquiry: (show) => {
      dispatch({ type: ACTIONS.SET_SHOW_NEW_ENQUIRY, payload: show });
    },
    
    setShowIncidentReport: (show) => {
      dispatch({ type: ACTIONS.SET_SHOW_INCIDENT_REPORT, payload: show });
    },
    
    // Notifications
    addNotification: (notification) => {
      dispatch({ type: ACTIONS.ADD_NOTIFICATION, payload: notification });
    },
    
    updateNotification: (id, updates) => {
      dispatch({ 
        type: ACTIONS.UPDATE_NOTIFICATION, 
        payload: { id, updates } 
      });
    },
    
    // Bulk Orders
    addBulkOrder: (order) => {
      dispatch({ type: ACTIONS.ADD_BULK_ORDER, payload: order });
    },
    
    removeBulkOrder: (orderId) => {
      dispatch({ type: ACTIONS.REMOVE_BULK_ORDER, payload: orderId });
    },
    
    // Forms
    setNoteType: (type) => {
      dispatch({ type: ACTIONS.SET_NOTE_TYPE, payload: type });
    },
    
    setNoteContent: (content) => {
      dispatch({ type: ACTIONS.SET_NOTE_CONTENT, payload: content });
    },
    
    setRejectionState: (state) => {
      dispatch({ type: ACTIONS.SET_REJECTION_STATE, payload: state });
    },
    
    setOrderStatusFilter: (filter) => {
      dispatch({ type: ACTIONS.SET_ORDER_STATUS_FILTER, payload: filter });
    },
    
    // System Context
    setCurrentSystem: (system) => {
      dispatch({ type: ACTIONS.SET_CURRENT_SYSTEM, payload: system });
    },
  };
};
